/* ImportScreenRenderer.js */

"use strict";
const ImportFileControl = require("@gluenetworks/steve_ui_core").Control.Action.InputFile;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const _ = require("lodash");
const {CommonDataValidator} = require("@gluenetworks/glue-common");

class ImportScreenRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("import-screen-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._importScreen = document.createElement("div");
        this._importScreen.setAttribute("type", "text");
        this._importScreen.setAttribute("draggable", true);

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Drop JSON Policy File to Import";

        this._buttonBarElem = document.createElement("div");
        this._buttonBarElem.classList.add("button-bar");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._importFromFile = new ImportFileControl({
            label: "Import from File",
            darkTheme: true,
        });

        this._importFromFile.setAttribute("style", "margin-right:15px;");

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._importScreen.addEventListener(
            "drop",
            (this._onImportFromFile = this._onImportFromFile.bind(this))
        );
        this._importScreen.addEventListener(
            "dragover",
            (this._onDragover = this._onDragover.bind(this))
        );
        this._importFromFile.on(
            "FileHandle",
            (this._onImportFromFile = this._onImportFromFile.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
    }
    _unregisterListeners() {
        this._importScreen.removeEventListener("drop", this._onImportFromFile);
        this._importScreen.removeEventListener("dragover", this._onDragover);
        this._importFromFile.removeListener("FileHandle", this._onImportFromFile);
        this._cancelButton.removeAllListeners("UserClick");
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._unregisterListeners();
        let promArr = [this._cancelButton.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._importFromFile = undefined;
                this._cancelButton = undefined;
                this._titleText.remove();
                this._titleText = undefined;
                this._importScreen.remove();
                this._importScreen = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ImportScreenRenderer.shutdown() error.");
            }
        );
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._importScreen);
                this._importScreen.appendChild(this._titleText);
                this._rootElem.appendChild(this._buttonBarElem);
                this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonBarElem.appendChild(this._importFromFile.renderer.rootElement);
            }
            resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onCancelButtonClick() {
        let err = new Error("user rejected");
        err.userRejected = true;
        this.model.cancelAction(err);
    }

    _onImportFromFile(event) {
        event.preventDefault();
        // check if event is drag'n'drop, or simple select file
        let fileList = event.dataTransfer ? event.dataTransfer.files : event.target.files;
        let file = fileList[0];
        let fileExtension = file.name.split(".").pop().toLowerCase();

        let fileHandler = (result) => {
            this._parseData(result.currentTarget.result, fileExtension)
                .then((jsonData) => {
                    for (const idx in jsonData) {
                        jsonData[idx].name = CommonDataValidator.sanitizeName(jsonData[idx].name);
                    }
                    this._model.sendImportData(jsonData);
                })
                .catch((err) => {
                    this._logger.warn(
                        {error: err},
                        "ImportScreenRenderer._onImportFromFile() error."
                    );
                    this._model.sendImportError(err);
                });
        };

        let fileReader = new FileReader();
        fileReader.onloadend = fileHandler;
        fileReader.readAsText(file);
    }

    _onDragover(evt) {
        evt.preventDefault();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _parseData(data, fileExtension) {
        return new Promise((resolve, reject) => {
            if (fileExtension === "json") {
                try {
                    let parsedJson = JSON.parse(data);
                    resolve(_.isArray(parsedJson) ? parsedJson : [parsedJson]);
                } catch (err) {
                    if (err.name && err.name === "SyntaxError") {
                        reject({
                            errorCode: "SyntaxError",
                            errorMsg: "Unable to parse JSON file.",
                        });
                    } else {
                        reject(err);
                    }
                }
            } else {
                this._logger.warn("Unknown file format for import: ", fileExtension);
                reject({
                    errorCode: "UnknownImportFileType",
                    errorMsg: "Unknown file type used for import.",
                });
            }
        });
    }
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = ImportScreenRenderer;
